import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import {
  ArrowRight,
  Check,
  CheckCircle,
  Clock,
  Copy,
  GraduationCap,
  Link,
  LucideAngularModule,
  XCircle,
  AlertCircle,
  Hourglass,
  Timer,
  FlaskConical,
} from 'lucide-angular';
import {
  AssignmentApiService,
  AssignmentResponse,
  SubmissionListItem,
} from '../assignment/services/assignment-api.service';
import {
  GradeDistributionChartComponent,
  GradeDistributionEntry,
  buildGradeDistribution,
  percentageToLetterGrade,
  getGradeLabelColorClass,
  LetterGrade,
} from '../../../common/components/grade-distribution-chart/grade-distribution-chart';
import { AmplitudeService } from '../../../common/analytics/amplitude.service';

@Component({
  selector: 'app-assignment-students',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, GradeDistributionChartComponent],
  templateUrl: './assignment-students.html',
  styleUrl: './assignment-students.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentStudentsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly apiService = inject(AssignmentApiService);
  private readonly amplitudeService = inject(AmplitudeService);

  protected readonly icons = {
    ArrowRight,
    Check,
    CheckCircle,
    Clock,
    Copy,
    GraduationCap,
    Link,
    XCircle,
    AlertCircle,
    Hourglass,
    Timer,
    FlaskConical,
  };

  protected readonly copied = signal(false);

  private readonly assignment$$ =
    new BehaviorSubject<AssignmentResponse | null>(null);
  protected readonly assignment$ = this.assignment$$.asObservable();

  private readonly submissions$$ = new BehaviorSubject<SubmissionListItem[]>(
    [],
  );
  protected readonly submissions$ = this.submissions$$.asObservable();

  private readonly loading$$ = new BehaviorSubject<boolean>(true);
  protected readonly loading$ = this.loading$$.asObservable();

  private readonly error$$ = new BehaviorSubject<string | null>(null);
  protected readonly error$ = this.error$$.asObservable();

  private assignmentId: string | null = null;

  ngOnInit(): void {
    this.assignmentId = this.route.snapshot.paramMap.get('id');
    if (!this.assignmentId) {
      this.router.navigate(['/teacher/dashboard/assignments']);
      return;
    }

    const id = this.assignmentId;

    this.apiService
      .getAssignment(id)
      .pipe(
        switchMap((assignment) => {
          this.assignment$$.next(assignment);
          return this.apiService.getAssignmentSubmissions(id);
        }),
        catchError(() => {
          this.error$$.next(
            'Failed to load data. The assignment may not exist or you may not have access.',
          );
          return of([]);
        }),
        finalize(() => this.loading$$.next(false)),
      )
      .subscribe((submissions) => {
        this.submissions$$.next(submissions);
      });
  }

  protected getAssignmentTitle(assignment: AssignmentResponse): string {
    return (
      assignment.title?.trim() ||
      assignment.objective?.trim() ||
      'Untitled assignment'
    );
  }

  /**
   * Display name: prefer studentName, fall back to email
   */
  protected getDisplayName(submission: SubmissionListItem): string {
    if (submission.studentName?.trim()) {
      return submission.studentName.trim();
    }
    return submission.studentEmail || 'Unknown student';
  }

  /**
   * Letter grade from percentage (e.g. "A", "B+")
   */
  protected getLetterGrade(submission: SubmissionListItem): string {
    if (submission.gradePercentage === null) return '—';
    return percentageToLetterGrade(submission.gradePercentage);
  }

  /**
   * Color classes for the grade badge background & text
   */
  protected getGradeBadgeClasses(submission: SubmissionListItem): string {
    if (submission.status === 'in_progress') {
      return 'bg-amber-100 text-amber-600';
    }
    if (
      submission.status === 'submitted' ||
      submission.gradePercentage === null
    ) {
      return 'bg-gray-100 text-gray-500';
    }
    const grade = percentageToLetterGrade(submission.gradePercentage);
    switch (grade) {
      case 'A':
        return 'bg-emerald-100 text-emerald-700';
      case 'B':
        return 'bg-green-100 text-green-700';
      case 'C':
        return 'bg-yellow-100 text-yellow-700';
      case 'D':
        return 'bg-orange-100 text-orange-700';
      case 'F':
        return 'bg-red-100 text-red-700';
    }
  }

  /**
   * Format a date string to absolute format: "Feb 10, 14:30"
   */
  protected formatAbsoluteTime(dateStr: string | null): string {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  /**
   * Compute human-readable duration between two date strings
   */
  protected getDuration(
    startStr: string | null,
    endStr: string | null,
  ): string | null {
    if (!startStr || !endStr) return null;
    const start = new Date(startStr);
    const end = new Date(endStr);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return null;

    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min`;
    if (hours > 0) return `${hours}h`;
    if (minutes > 0) return `${minutes}min`;
    return '<1min';
  }

  /**
   * Points display: "4/5 pts"
   */
  protected getPointsLabel(submission: SubmissionListItem): string | null {
    if (
      submission.totalPointsAwarded !== null &&
      submission.totalPointsPossible !== null
    ) {
      return `${submission.totalPointsAwarded}/${submission.totalPointsPossible} pts`;
    }
    return null;
  }

  /**
   * Color class for the grade percentage text
   */
  protected getGradePercentageClass(submission: SubmissionListItem): string {
    if (submission.gradePercentage === null) return 'text-gray-500';
    const pct = submission.gradePercentage;
    if (pct >= 90) return 'text-emerald-600';
    if (pct >= 80) return 'text-green-600';
    if (pct >= 70) return 'text-yellow-600';
    if (pct >= 60) return 'text-orange-600';
    return 'text-red-600';
  }

  protected viewSubmission(submission: SubmissionListItem): void {
    if (!this.assignmentId) return;
    this.router.navigate([
      '/assignment',
      this.assignmentId,
      'submission',
      submission.id,
    ]);
  }

  protected onTestAssignment(): void {
    const assignment = this.assignment$$.value;
    if (!assignment?.shareCode) return;
    this.amplitudeService.trackEvent('assignment_test_clicked', {
      assignment_id: assignment.id,
      source: 'assignment_students',
    });
    this.router.navigate(['/a', assignment.shareCode]);
  }

  protected getShareLink(): string {
    const assignment = this.assignment$$.value;
    if (!assignment?.shareCode) return '';
    const baseUrl =
      typeof window !== 'undefined' ? window.location.origin : '';
    return `${baseUrl}/a/${assignment.shareCode}`;
  }

  protected async copyShareLink(): Promise<void> {
    const link = this.getShareLink();
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    } catch {
      // Fallback: ignore silently
    }
  }

  /**
   * Build grade distribution from graded submissions
   */
  protected getGradeDistribution(
    submissions: SubmissionListItem[],
  ): GradeDistributionEntry[] {
    const gradedPercentages = submissions
      .filter((s) => s.status === 'graded' && s.gradePercentage !== null)
      .map((s) => s.gradePercentage);
    return buildGradeDistribution(gradedPercentages);
  }

  /**
   * Compute the average letter grade from graded submissions
   */
  protected getAverageGrade(
    submissions: SubmissionListItem[],
  ): LetterGrade | null {
    const graded = submissions.filter(
      (s) => s.status === 'graded' && s.gradePercentage !== null,
    );
    if (graded.length === 0) return null;
    const avg =
      graded.reduce((sum, s) => sum + s.gradePercentage!, 0) / graded.length;
    return percentageToLetterGrade(avg);
  }

  /**
   * Get color class for the average grade label
   */
  protected getAverageGradeColorClass(grade: LetterGrade): string {
    return getGradeLabelColorClass(grade);
  }
}
